import ActionCable from "actioncable";
import Cookies from "js-cookie";

import humps from "humps";
import { jwtDecode } from "jwt-decode";
import { getCurrentSelectedTenant, getIsSjAdminFromStorage } from "../utils";
import { Attempt, LessonAttempt } from "../api/types/tournamentTypes";

const isLocal = import.meta.env.VITE_APP_ENVIRONMENT === "local";
const useSecureCookie =
  !isLocal && import.meta.env.VITE_APP_ENVIRONMENT !== "test" && !import.meta.env.CI;

const authToken = localStorage.getItem("authToken");
const currentSelectedTenant = getCurrentSelectedTenant();
const isSjAdmin = !!getIsSjAdminFromStorage();

let cable: ActionCable.Cable | undefined;

interface JwtPayload {
  available_tenants?: Array<{ name: string }>;
}

// Channel message type definitions
export interface CodeSubmissionChannelMessage {
  status: string;
  attempt: LessonAttempt;
  email: string;
}

export interface CtfSubmissionChannelMessage {
  status: string;
  attempt: LessonAttempt;
  email: string;
}

export interface LessonCompletedChannelMessage {
  status: string;
  entity: Entity;
  email: string;
}

export interface TournamentExperimentMessage extends Attempt {
  attemptStatus: string;
  email: string;
  points: number;
}

export interface PathCompletedChannelMessage {
  status: string;
  roleEnrollmentId: number;
}

interface Entity {
  id: number;
  right_answers_count: number | null;
  result: string;
  display_score: string;
  assessment_score: number;
  show_after_survey: boolean;
  show_before_survey: boolean;
  answers_count: number;
  questions_count: number;
}

const findTenant = (): string | null => {
  if (currentSelectedTenant) {
    return currentSelectedTenant;
  }
  if (!authToken) {
    return null;
  }
  const decoded = jwtDecode<JwtPayload>(authToken);
  const tenants = decoded.available_tenants;
  if (tenants?.length) {
    return tenants[0]?.name ?? null;
  } else {
    return null;
  }
};

const ensureCable = (): void => {
  const baseParams = [
    "impersonate_authorization=",
    `current_selected_tenant=${findTenant()}`,
    `is_sj_admin=${isSjAdmin}`,
  ];

  if (authToken) {
    Cookies.set("authToken", authToken, {
      sameSite: "strict",
      expires: new Date(Date.now() + 60 * 1000),
      secure: useSecureCookie,
    });
  }

  const url = `/api/cable?${baseParams.join("&")}`;

  if (!cable) {
    cable = ActionCable.createConsumer(url);
  }
};

interface InitCableOptions {
  onDisconnect?: () => void;
  onConnect?: () => void;
  camelizeKeys?: boolean;
}

export const initCable = <T = unknown>(
  channelName: string,
  onReceive: (data: T) => void,
  { onDisconnect, onConnect, camelizeKeys = false }: InitCableOptions = {}
): ActionCable.Channel => {
  ensureCable();
  return cable!.subscriptions.create(
    { channel: channelName },
    {
      connected: () => {
        console.log(`${channelName} connected`);
        if (onConnect) {
          onConnect();
        }
      },

      disconnected: () => {
        console.log(`${channelName} disconnected`);
        if (onDisconnect) {
          onDisconnect();
        }
      },

      received: (data: object) => {
        if (camelizeKeys) {
          data = humps.camelizeKeys(data);
        }
        onReceive(data as T);
      },
    }
  );
};

export const NOTIFICATIONS_CHANNEL = "NotificationsChannel";
export const DOJO_ACTIVITY_CHANNEL = "DojoActivityChannel";
export const CUSTOM_NOTIFICATIONS_CHANNEL = "CustomNotificationsChannel";
export const MAYHEM_CHANNEL = "MayhemChannel";
export const IMPORT_USERS_CHANNEL = "ImportUsersChannel";
export const IMPORT_FROM_HEROKU_CHANNEL = "ImportFromHerokuChannel";
export const IMPORT_FROM_HACKEDU_CHANNEL = "ImportFromHackeduChannel";
export const UPLOAD_ASSIGNMENT_USERS_CHANNEL = "UploadAssignmentUsersChannel";
export const CREATE_VIRTUAL_DOJO_CHANNEL = "CreateVirtualDojoChannel";
export const CREATE_ASSIGNMENTS_CHANNEL = "CreateAssignmentsChannel";
export const UNLOCK_ALL_USERS_CHANNEL = "UnlockAllUsersChannel";
export const SYNCHRONIZE_CONTENT_CHANNEL = "SynchronizeContentChannel";
export const CREATE_LEVEL_WITH_PREFERENCES_CHANNEL = "CreateLevelWithPreferencesChannel";
export const DOWNLOAD_NOTES_CHANNEL = "DownloadNotesChannel";
export const VIRTUAL_DOJO_CURRENT_STATUS_CHANNEL = "VirtualDojoCurrentStatusChannel";
export const LESSON_COMPLETED_CHANNEL = "LessonCompletedChannel";
export const CODE_SUBMISSION_CHANNEL = "CodeSubmissionChannel";
export const CTF_SUBMISSION_CHANNEL = "CtfSubmissionChannel";
export const EXPERIMENT_CODE_SUBMISSION_CHANNEL = "ExperimentCodeSubmissionChannel";
export const TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL =
  "TournamentExperimentCodeSubmissionChannel";
export const ARCHIVE_USER_CHANNEL = "ArchiveUserChannel";
export const PATH_COMPLETED_CHANNEL = "PathCompletedChannel";

export const initLessonCompletedChannel = (
  onReceive: (data: LessonCompletedChannelMessage) => void
): ActionCable.Channel => {
  return initCable(LESSON_COMPLETED_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initCodeSubmissionChannel = (
  onReceive: (data: CodeSubmissionChannelMessage) => void
): ActionCable.Channel => {
  return initCable(CODE_SUBMISSION_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initCtfSubmissionChannel = (
  onReceive: (data: CtfSubmissionChannelMessage) => void
): ActionCable.Channel => {
  return initCable(CTF_SUBMISSION_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initTournamentExperimentCodeSubmissionChannel = (
  onReceive: (data: TournamentExperimentMessage) => void
): ActionCable.Channel => {
  return initCable(TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL, onReceive, {
    camelizeKeys: true,
  });
};

export const initNotificationsChannel = (
  onReceive: (data: { status: boolean }) => void
): ActionCable.Channel => {
  return initCable(NOTIFICATIONS_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initPathCompletedChannel = (
  onReceive: (data: PathCompletedChannelMessage) => void
): ActionCable.Channel => {
  return initCable(PATH_COMPLETED_CHANNEL, onReceive, { camelizeKeys: true });
};
